import React, { FunctionComponent } from 'react';
import { isArray } from '@/utils/isArray';
import { Accordion, AccordionSection, Col, LinkButton, Row } from '@visalex/core';

import cx from 'clsx';
import { useTranslation } from 'next-i18next';

import styles from './GreenCard.module.scss';

export type GreenCardProps = {
  imageUrl: string;
  imageAlt: string;
  title: string;
  description: string;
  subtitle: string;
  columns: GreenCardColumn[];
  link: string;
};

export type GreenCardElement = {
  type: string;
  text: string;
  subtext: string;
};

export type GreenCardColumn = {
  title: string;
  elements: GreenCardElement[];
  text: string;
};

const GreenCard: FunctionComponent<GreenCardProps> = ({
  imageUrl,
  imageAlt,
  title,
  description,
  subtitle,
  columns,
  link,
}) => {
  const { t } = useTranslation();

  // because tests can not run translation function we need to manually mock these values
  if (process.env.NODE_ENV === 'test') {
    imageUrl = '/';
  }

  return (
    <div className={styles['green-card']}>
      <Row className={styles['green-card__hero']}>
        <Col xs={12} md={10} lg={6} className="order-lg-0 order-1">
          <h3 className={styles['green-card__hero_title']} dangerouslySetInnerHTML={{ __html: title }}></h3>
          <p className={styles['green-card__hero_description']}>{description}</p>
          <LinkButton href={link} outline className={styles['green-card__hero_button']}>
            {t('green-cards.CTA-help-me')}
          </LinkButton>
        </Col>
        <Col xs={12} md={10} lg={6} className="order-lg-1 order-0">
          <img src={imageUrl} alt={imageAlt} className={styles['green-card__hero_img']} />
        </Col>
      </Row>
      <Row>
        <Col>
          <p className={styles['green-card__title']}>{subtitle}</p>
        </Col>
      </Row>
      <Row className={styles['green-card__lists']}>
        {isArray(columns) &&
          columns.map((column: GreenCardColumn, keyGreenCardColumn: number) => (
            <Col xs={12} lg={6} key={keyGreenCardColumn} className={styles['green-card__lists_col']}>
              <Accordion
                classNames={{
                  accordion: styles['accordion-list'],
                  sectionHeader: styles['accordion-list__title'],
                  sectionContent: styles['accordion-list__body'],
                }}
              >
                {[0].map((id: number) => (
                  <AccordionSection key={id} index={id} header={<span>{column.title}</span>}>
                    {column.elements &&
                      column.elements.map((row: GreenCardElement, keyRow: number) => (
                        <div className={styles['accordion-list__row']} key={keyRow}>
                          <div
                            className={cx(
                              styles['accordion-list__row-title'],
                              row.type && styles['accordion-list__row-title--fixed']
                            )}
                          >
                            {row.type ? row.type : '•'}
                          </div>
                          <div className={styles['accordion-list__row-value']}>
                            {row.text}
                            {row.subtext && <span>{row.subtext}</span>}
                          </div>
                        </div>
                      ))}

                    {column.text && (
                      <div className={styles['accordion-list__row']}>
                        <div className={styles['accordion-list__row-value']}>{column.text}</div>
                      </div>
                    )}
                  </AccordionSection>
                ))}
              </Accordion>
            </Col>
          ))}
      </Row>
    </div>
  );
};

export default GreenCard;
